/**
 * SELECT PESQUISÁVEL (combobox com typeahead).
 *
 * Existe porque duas listas deste sistema não cabem num `<select>` comum: a
 * de temperaturas tem 251 linhas, e obrigar alguém a rolar até "120 °C" é
 * pedir erro de digitação em documento técnico. `AppSelect` chegou a declarar
 * um atributo `searchable`, mas nunca o implementou — era um `<select>` puro.
 *
 * O que o componente garante:
 *
 *   - digitar filtra a lista (sem acento, sem caixa, imediato);
 *   - teclado completo — ↑ ↓ Home End Enter Esc Tab;
 *   - mouse;
 *   - SÓ FECHA COM VALOR DO CATÁLOGO. Texto digitado que não corresponde a
 *     nenhuma opção é descartado ao sair do campo, e o valor anterior volta.
 *     É o "não permitir salvar texto não existente" do comando, na interface;
 *     a validação de verdade continua no domínio e no serviço, porque
 *     esconder ou reverter na tela nunca foi controle de nada.
 *
 * Uso no HTML (a página só escreve a tag; o componente se popula sozinho):
 *
 *   <searchable-select data-source="material-family" value="CARBON_STEEL">
 *   <searchable-select data-source="temperature" value="120">
 *
 * O `data-source` aponta para um provedor registrado abaixo. A página é
 * re-renderizada por innerHTML a cada mudança, então o componente não pode
 * depender de alguém lhe atribuir opções por propriedade depois — ele se
 * reconstrói a partir dos atributos, e é por isso que a fonte é declarativa.
 */

import {
  ME_MATERIAL_FAMILIES,
  ME_TEMPERATURE_OPTIONS,
  formatTemperatureC,
  searchMaterialFamilies,
  searchTemperatures
} from "../../domain/engineering/me-thermal-correction.js";

export interface SearchableOption {
  readonly value: string;
  readonly label: string;
  /** Texto auxiliar à direita (unidade, código, situação). */
  readonly hint?: string;
}

export interface SearchableSource {
  /** Todas as opções, para a lista fechada e para resolver um rótulo. */
  all(): readonly SearchableOption[];
  /** Filtro digitado. Termo vazio devolve tudo. */
  search(query: string): readonly SearchableOption[];
}

const SOURCES = new Map<string, SearchableSource>();

/** Registra uma fonte para `data-source`. Chamável por qualquer módulo. */
export function registerSearchableSource(name: string, source: SearchableSource): void {
  SOURCES.set(name, source);
}

registerSearchableSource("material-family", {
  all: () => ME_MATERIAL_FAMILIES.map(f => ({ value: f.code, label: f.label })),
  search: (query: string) => searchMaterialFamilies(query).map(f => ({ value: f.code, label: f.label }))
});

registerSearchableSource("temperature", {
  all: () => ME_TEMPERATURE_OPTIONS.map(t => ({ value: String(t), label: formatTemperatureC(t) })),
  search: (query: string) => searchTemperatures(query).map(t => ({ value: String(t), label: formatTemperatureC(t) }))
});

const STYLE_ID = "searchable-select-styles";

function ensureStyles(): void {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    searchable-select { position: relative; display: block; }
    searchable-select .ss-input {
      width: 100%; box-sizing: border-box;
      background: var(--color-input-bg, #1e1e22);
      border: 1px solid var(--color-input-border, #3a3a3f);
      border-radius: 6px; padding: 7px 26px 7px 10px;
      color: var(--color-text-primary, #EDEDED); font-size: 0.82rem;
    }
    searchable-select .ss-input:focus { outline: none; border-color: var(--color-accent, #C8A24A); }
    searchable-select .ss-input[disabled] { opacity: .6; cursor: not-allowed; }
    searchable-select .ss-caret {
      position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
      pointer-events: none; font-size: 0.6rem; color: var(--ia-fg-soft, #9A9A9A);
    }
    searchable-select .ss-list {
      position: absolute; z-index: 60; left: 0; right: 0; top: calc(100% + 2px);
      max-height: 240px; overflow-y: auto; margin: 0; padding: 4px; list-style: none;
      background: var(--color-surface-2, #26262B);
      border: 1px solid var(--color-input-border, #3a3a3f);
      border-radius: 6px; box-shadow: 0 8px 24px rgba(0,0,0,.45);
    }
    searchable-select .ss-list[hidden] { display: none; }
    searchable-select .ss-option {
      padding: 6px 9px; border-radius: 4px; cursor: pointer;
      font-size: 0.8rem; color: var(--color-text-primary, #EDEDED);
      display: flex; justify-content: space-between; gap: 10px;
    }
    searchable-select .ss-option[aria-selected="true"] { font-weight: 600; }
    searchable-select .ss-option.ss-active { background: var(--color-accent, #C8A24A); color: #1A1A1A; }
    searchable-select .ss-option .ss-hint { opacity: .65; font-size: 0.72rem; }
    searchable-select .ss-empty { padding: 8px 9px; font-size: 0.76rem; color: var(--ia-fg-soft, #9A9A9A); }
  `;
  document.head.appendChild(style);
}

export class SearchableSelect extends HTMLElement {
  private options: readonly SearchableOption[] = [];
  private filtered: readonly SearchableOption[] = [];
  private activeIndex = -1;
  private open = false;
  /** O último valor VÁLIDO. É para cá que o campo volta se o texto não casar. */
  private committed = "";
  private input: HTMLInputElement | null = null;
  private list: HTMLUListElement | null = null;

  static get observedAttributes() {
    return ["value", "disabled", "placeholder", "data-source"];
  }

  connectedCallback() {
    ensureStyles();
    this.build();
  }

  attributeChangedCallback(name: string, previous: string | null, next: string | null) {
    if (previous === next || !this.input) return;
    if (name === "value") {
      this.committed = next || "";
      this.input.value = this.labelOf(this.committed);
    } else {
      this.build();
    }
  }

  get value(): string {
    return this.committed;
  }

  set value(next: string) {
    this.committed = next || "";
    if (this.input) this.input.value = this.labelOf(this.committed);
    this.setAttribute("value", this.committed);
  }

  private source(): SearchableSource | null {
    return SOURCES.get(this.dataset["source"] || "") ?? null;
  }

  private labelOf(value: string): string {
    if (!value) return "";
    return this.options.find(o => o.value === value)?.label ?? value;
  }

  private build(): void {
    const source = this.source();
    this.options = source ? source.all() : [];
    this.committed = this.getAttribute("value") || "";

    const disabled = this.hasAttribute("disabled");
    const placeholder = this.getAttribute("placeholder") || "Digite para pesquisar…";

    this.innerHTML = `
      <input class="ss-input" type="text" role="combobox" autocomplete="off" spellcheck="false"
             aria-expanded="false" aria-autocomplete="list"
             placeholder="${escapeAttribute(placeholder)}"
             value="${escapeAttribute(this.labelOf(this.committed))}" ${disabled ? "disabled" : ""}>
      <span class="ss-caret">▼</span>
      <ul class="ss-list" role="listbox" hidden></ul>
    `;

    this.input = this.querySelector(".ss-input");
    this.list = this.querySelector(".ss-list");
    if (!this.input || !this.list || disabled) return;

    this.input.addEventListener("focus", () => this.openList(""));
    this.input.addEventListener("input", () => this.openList(this.input?.value || ""));
    this.input.addEventListener("keydown", e => this.onKeyDown(e));
    // `mousedown` e não `click`: o blur do input dispara antes do click e
    // fecharia a lista, cancelando a escolha feita com o mouse.
    this.list.addEventListener("mousedown", e => {
      const option = (e.target as HTMLElement).closest<HTMLElement>(".ss-option");
      if (!option) return;
      e.preventDefault();
      this.commit(option.dataset["value"] || "");
    });
    this.input.addEventListener("blur", () => {
      // Sair do campo com texto que não é opção devolve o último valor
      // válido, em vez de deixar meio nome escrito parecendo selecionado.
      this.closeList();
      if (this.input) this.input.value = this.labelOf(this.committed);
    });
  }

  private openList(query: string): void {
    const source = this.source();
    if (!source || !this.list) return;
    this.filtered = source.search(query);
    // Com a lista inteira aberta, o item já escolhido é o ponto de partida
    // da navegação por teclado — não a primeira linha de 251.
    const selectedIndex = this.filtered.findIndex(o => o.value === this.committed);
    this.activeIndex = this.filtered.length === 0 ? -1 : (query ? 0 : Math.max(selectedIndex, 0));
    this.open = true;
    this.renderList();
    this.input?.setAttribute("aria-expanded", "true");
  }

  private closeList(): void {
    this.open = false;
    if (this.list) this.list.hidden = true;
    this.input?.setAttribute("aria-expanded", "false");
  }

  private renderList(): void {
    if (!this.list) return;
    if (!this.open) { this.list.hidden = true; return; }

    if (this.filtered.length === 0) {
      this.list.innerHTML = `<li class="ss-empty">Nenhuma opção corresponde à pesquisa.</li>`;
      this.list.hidden = false;
      return;
    }

    this.list.innerHTML = this.filtered.map((option, index) => `
      <li class="ss-option ${index === this.activeIndex ? "ss-active" : ""}"
          role="option" data-value="${escapeAttribute(option.value)}"
          aria-selected="${option.value === this.committed ? "true" : "false"}">
        <span>${escapeText(option.label)}</span>
        ${option.hint ? `<span class="ss-hint">${escapeText(option.hint)}</span>` : ""}
      </li>
    `).join("");
    this.list.hidden = false;
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    const active = this.list?.querySelector<HTMLElement>(".ss-active");
    active?.scrollIntoView({ block: "nearest" });
  }

  private move(delta: number): void {
    if (this.filtered.length === 0) return;
    const next = this.activeIndex + delta;
    this.activeIndex = Math.min(Math.max(next, 0), this.filtered.length - 1);
    this.renderList();
  }

  private onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.open) this.openList(this.input?.value || ""); else this.move(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        if (this.open) this.move(-1);
        return;
      case "Home":
        if (this.open) { event.preventDefault(); this.activeIndex = 0; this.renderList(); }
        return;
      case "End":
        if (this.open) { event.preventDefault(); this.activeIndex = this.filtered.length - 1; this.renderList(); }
        return;
      case "Enter": {
        if (!this.open) return;
        event.preventDefault();
        const chosen = this.filtered[this.activeIndex];
        if (chosen) this.commit(chosen.value);
        return;
      }
      case "Tab": {
        // Tab confirma a opção em destaque e segue o fluxo normal do
        // formulário: sair do campo não pode desfazer o que estava escolhido.
        if (this.open && this.activeIndex >= 0) {
          const chosen = this.filtered[this.activeIndex];
          if (chosen) this.commit(chosen.value);
        }
        return;
      }
      case "Escape":
        if (this.open) {
          event.preventDefault();
          this.closeList();
          if (this.input) this.input.value = this.labelOf(this.committed);
        }
        return;
      default:
    }
  }

  /**
   * Confirma uma opção do catálogo. Só daqui sai `searchable-change`, então
   * nenhum consumidor recebe texto que o usuário apenas digitou.
   */
  private commit(value: string): void {
    const option = this.options.find(o => o.value === value);
    if (!option) return;

    const changed = option.value !== this.committed;
    this.committed = option.value;
    this.setAttribute("value", option.value);
    if (this.input) this.input.value = option.label;
    this.closeList();

    if (!changed) return;
    this.dispatchEvent(new CustomEvent("searchable-change", {
      bubbles: true,
      composed: true,
      detail: { value: option.value, label: option.label, field: this.dataset["field"] || "" }
    }));
  }
}

function escapeAttribute(value: string): string {
  return escapeText(value).replace(/"/g, "&quot;");
}

function escapeText(value: string): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

if (typeof customElements !== "undefined" && !customElements.get("searchable-select")) {
  customElements.define("searchable-select", SearchableSelect);
}
